import type { BriefReadinessState } from "../../enums/brief";
import { BRIEF_READINESS_STATE } from "../../enums/brief";
import type { DeliveryBrief } from "./briefTypes";

type BriefReadinessItem = {
  id: string;
  label: string;
  state: BriefReadinessState;
  detail: string;
};

export function briefReadiness(brief: DeliveryBrief): BriefReadinessItem[] {
  const outcomeProvided =
    hasText(brief.objective) && hasText(brief.requiredChange);
  const scopeRecorded = brief.pages.length > 0 || hasText(brief.scopeNotes);
  const requirementsProvided = brief.requirements.some((block) =>
    block.items.some(hasText),
  );
  const criteriaProvided =
    brief.acceptanceCriteria.length > 0 &&
    brief.acceptanceCriteria.every((criterion) => hasText(criterion));
  const ownerAssigned = hasAssignedOwner(brief.owner);
  const responsesRecorded = brief.openQuestions.every(isQuestionResolved);

  return [
    readiness("outcome", "Objective and change provided", outcomeProvided),
    readiness("scope", "Scope recorded", scopeRecorded),
    readiness("requirements", "Requirements provided", requirementsProvided),
    readiness("criteria", "Acceptance criteria provided", criteriaProvided),
    readiness("owner", "Owner recorded", ownerAssigned),
    readiness(
      "inputs",
      brief.openQuestions.length
        ? "Question responses recorded"
        : "No open questions listed",
      responsesRecorded,
    ),
  ];
}

export function canMarkBriefReady(brief: DeliveryBrief): boolean {
  return briefReadiness(brief).every(
    (item) => item.state === BRIEF_READINESS_STATE.COMPLETE,
  );
}

export function completedReadinessItems(brief: DeliveryBrief): number {
  return briefReadiness(brief).filter(
    (item) => item.state === BRIEF_READINESS_STATE.COMPLETE,
  ).length;
}

export function isQuestionResolved(
  question: DeliveryBrief["openQuestions"][number],
): boolean {
  return hasText(question.response);
}

function readiness(
  id: string,
  label: string,
  complete: boolean,
): BriefReadinessItem {
  return {
    id,
    label,
    state: complete
      ? BRIEF_READINESS_STATE.COMPLETE
      : BRIEF_READINESS_STATE.INCOMPLETE,
    detail: complete ? "Provided" : "Needs input",
  };
}

function hasText(value: string): boolean {
  return value.trim().length > 0;
}

function hasAssignedOwner(value: string): boolean {
  const owner = value.trim().toLocaleLowerCase();
  return owner.length > 0 && owner !== "not assigned" && owner !== "unassigned";
}
